<template>
  <div class="home">
    <section class="hero">
      <div class="hero-bg"></div>
      <div class="hero-vignette"></div>

      <div class="hero-content">
        <p class="hero-eyebrow">Ierapetra · Crete · Greece</p>
        <h1 class="hero-title">
          <span class="hero-line">Where</span>
          <span class="hero-line italic">luxury</span>
          <span class="hero-line">meets the sea</span>
        </h1>
        <div class="hero-divider"></div>
        <p class="hero-sub">Six private villas on the southern coast of Crete.</p>
        <div class="hero-actions">
          <a href="/our-villas" class="btn btn-outline-white">Our villas</a>
          <a href="https://proistakisvillas.reserve-online.net" class="btn btn-gold" target="_blank" rel="noopener">Book a stay</a>
        </div>
      </div>

      <div class="hero-scroll">
        <span class="hero-scroll-line"></span>
        <span class="hero-scroll-label">Scroll</span>
      </div>
    </section>
    <section class="villas-section">
      <div class="villas-header" v-reveal>
        <span class="eyebrow">Our collection</span>
        <h2 class="section-title">Six villas,<br><em>one coastline</em></h2>
      </div>

      <div class="villas-grid">
        <a
          v-for="(villa, i) in villas"
          :key="villa.name"
          :href="`/villa/${villa.name}`"
          class="villa-card"
          :class="{ featured: i === 0 }"
          v-reveal="{ delay: i * 80 }"
        >
          <div class="villa-card-img">
            <img
              :src="`/villas_photos/${villa.name}/image-${villa.preview_image}.webp`"
              :alt="villa.label"
            />
            <div class="villa-card-overlay">
              <span class="villa-card-cta">View villa →</span>
            </div>
          </div>
          <div class="villa-card-body">
            <div class="villa-card-meta">
              <span>{{ villa.info.bedrooms }} bed</span>
              <span class="sep">·</span>
              <span>{{ villa.info.bathrooms }} bath</span>
              <span v-if="villa.info.beachfront" class="sep">·</span>
              <span v-if="villa.info.beachfront" class="tag-beach">Beachfront</span>
            </div>
            <h3 class="villa-card-name">{{ villa.label }}</h3>
          </div>
        </a>
      </div>
    </section>
    <section class="split-section">
      <div class="split-image">
        <img src="/villas_photos/mystique_luxury_suite/image-3.webp" alt="Waterfront villa" />
      </div>
      <div class="split-body" v-reveal>
        <span class="eyebrow">On the water's edge</span>
        <h2 class="split-title">Waterfront<br><em>villas</em></h2>
        <div class="split-line"></div>
        <p class="split-text">Wake up to the Aegean. Several of our properties sit directly on the Cretan shore - private beach access, unobstructed sea views, and the sound of waves outside your window.</p>
        <a href="/our-villas" class="text-link">Explore all villas <font-awesome-icon icon="arrow-right" /></a>
      </div>
    </section>
    <section class="destinations-section">
      <div class="destinations-bg"></div>
      <div class="destinations-overlay"></div>
      <div class="destinations-content" v-reveal>
        <span class="eyebrow" style="color:rgba(201,169,108,0.7)">Discover the island</span>
        <h2 class="destinations-title">Beyond<br><em>the villa</em></h2>
        <p class="destinations-sub">Chrissi Island. Myrtos Beach. Pelagos Seaside. The best of Crete is right outside your door.</p>
        <a href="/discover" class="btn btn-outline-white">Discover Crete</a>
      </div>
    </section>
    <section class="split-section reverse">
      <div class="split-body" v-reveal>
        <span class="eyebrow">A family tradition</span>
        <h2 class="split-title">Explore our<br><em>collection</em></h2>
        <div class="split-line"></div>
        <p class="split-text">Accommodation crafted for those who want to combine genuine Cretan warmth with one of the most extraordinary coastlines in the world. Not a hotel. Not a resort. A home.</p>
        <a href="/our-villas" class="text-link">Browse the collection <font-awesome-icon icon="arrow-right" /></a>
      </div>
      <div class="split-image">
        <img src="/villas_photos/villa_proistakis/image-1.webp" alt="Villa Proistakis" />
      </div>
    </section>
    <section class="cta-section" v-reveal>
      <div class="cta-inner">
        <span class="eyebrow" style="text-align:center;display:block">Get in touch</span>
        <h2 class="cta-title">Ready to plan<br><em>your stay?</em></h2>
        <p class="cta-sub">Talk to the host directly - we'll help you find the right villa for the right moment.</p>
        <div class="cta-actions">
          <a href="/contact" class="btn btn-gold">Contact us</a>
          <a href="https://proistakisvillas.reserve-online.net" class="btn btn-outline-gold" target="_blank" rel="noopener">Book online</a>
        </div>
      </div>
    </section>

  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';

export default defineComponent({
  name: 'HomeView',
  data() {
    return { villas: [] as any[] };
  },
  mounted() {
    // @ts-ignore
    this.villas = this.$root.getVillas();

    // Parallax on hero background
    const heroBg = this.$el.querySelector('.hero-bg') as HTMLElement | null;
    if (heroBg) {
      const onScroll = () => {
        const y = window.scrollY;
        heroBg.style.transform = `translateY(${y * 0.28}px)`;
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      (this as any)._heroParallaxOff = () => window.removeEventListener('scroll', onScroll);
    }
  },
  unmounted() {
    (this as any)._heroParallaxOff?.();
  }
});
</script>

<style lang="scss">
.hero {
  position: relative;
  min-height: 100dvh;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.hero-bg {
  position: absolute;
  inset: -20%;
  background-image: url(/public/villas_photos/best_1.webp);
  background-size: cover;
  background-position: center;
  animation: heroZoom 14s cubic-bezier(0.4,0,0.2,1) forwards;
  z-index: 0;
  will-change: transform;
}

@keyframes heroZoom {
  from { transform: scale(1.08); filter: brightness(0.55); }
  to   { transform: scale(1);    filter: brightness(0.45); }
}

.hero-vignette {
  position: absolute;
  inset: 0;
  z-index: 1;
  background:
    radial-gradient(ellipse at center, transparent 30%, rgba(12,10,8,0.7) 100%),
    linear-gradient(to top, rgba(12,10,8,0.9) 0%, transparent 40%),
    linear-gradient(to bottom, rgba(12,10,8,0.5) 0%, transparent 30%);
}

.hero-content {
  position: relative;
  z-index: 2;
  text-align: center;
  padding: 0 1.5rem;
}

.hero-eyebrow {
  font-size: 0.7rem;
  font-weight: 500;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--gold);
  margin-bottom: 1.6rem;
  animation: fadeUp 1s ease both;
  animation-delay: 0.4s;
}

.hero-title {
  font-family: var(--serif);
  font-weight: 300;
  font-size: clamp(4rem, 9.5vw, 10rem);
  line-height: 0.95;
  letter-spacing: -0.02em;
  color: var(--text);
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 2rem;
}

.hero-line {
  display: block;
  animation: fadeUp 1s ease both;
  &:nth-child(1) { animation-delay: 0.55s; }
  &:nth-child(2) { animation-delay: 0.7s; }
  &:nth-child(3) { animation-delay: 0.85s; }
}

.hero-line.italic {
  font-style: italic;
  font-weight: 300;
  color: var(--gold);
}

.hero-divider {
  width: 40px;
  height: 1px;
  background: var(--gold);
  margin: 0 auto 1.8rem;
  opacity: 0.6;
  animation: fadeUp 1s ease both;
  animation-delay: 1s;
}

.hero-sub {
  font-size: 1rem;
  color: rgba(237,230,216,0.6);
  letter-spacing: 0.04em;
  margin-bottom: 2.8rem;
  animation: fadeUp 1s ease both;
  animation-delay: 1.1s;
}

.hero-actions {
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
  animation: fadeUp 1s ease both;
  animation-delay: 1.25s;
}

@keyframes fadeUp {
  from { opacity: 0; transform: translateY(22px); }
  to   { opacity: 1; transform: translateY(0); }
}

.hero-scroll {
  position: absolute;
  bottom: 2.5rem;
  left: 50%;
  transform: translateX(-50%);
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  animation: fadeUp 1s ease both;
  animation-delay: 1.6s;
}

.hero-scroll-line {
  display: block;
  width: 1px;
  height: 50px;
  background: linear-gradient(to bottom, transparent, var(--gold));
  animation: scrollPulse 2.4s ease-in-out infinite;
}

@keyframes scrollPulse {
  0%, 100% { opacity: 0.3; transform: scaleY(0.6); }
  50%       { opacity: 1;   transform: scaleY(1); }
}

.hero-scroll-label {
  font-size: 0.62rem;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: rgba(201,169,108,0.5);
}

.section-title {
  font-family: var(--serif);
  font-size: clamp(2.8rem, 5.5vw, 5rem);
  font-weight: 300;
  line-height: 1.05;
  letter-spacing: -0.02em;
  color: var(--text);

  em { font-style: italic; color: var(--gold); font-weight: 300; }
}

.villas-section {
  padding: 8rem 2.5rem;
  max-width: var(--max-w);
  margin: 0 auto;
}

.villas-header {
  margin-bottom: 4rem;
}

.villas-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5px;
  background: var(--line);
}

.villa-card {
  position: relative;
  overflow: hidden;
  display: block;
  background: var(--layer);
  text-decoration: none;

  &.featured {
    grid-row: span 2;
  }
}

.villa-card-img {
  position: relative;
  overflow: hidden;
  aspect-ratio: 4/3;

  .featured & {
    aspect-ratio: unset;
    height: 100%;
    min-height: 460px;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.9s var(--ease);
  }
}

.villa-card:hover .villa-card-img img {
  transform: scale(1.05);
}

.villa-card-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(to top, rgba(12,10,8,0.85) 0%, transparent 55%);
  display: flex;
  align-items: flex-end;
  padding: 1.4rem;
  opacity: 0;
  transition: opacity var(--t) var(--ease);
}

.villa-card:hover .villa-card-overlay {
  opacity: 1;
}

.villa-card-cta {
  font-size: 0.75rem;
  font-weight: 500;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--gold);
}

.villa-card-body {
  padding: 1.2rem 1.4rem 1.5rem;
  background: var(--layer);
  border-top: 1px solid var(--line);
}

.villa-card-meta {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.72rem;
  font-weight: 500;
  letter-spacing: 0.06em;
  color: var(--muted);
  margin-bottom: 0.4rem;
  text-transform: uppercase;

  .sep { color: var(--line); opacity: 2; }
}

.tag-beach {
  color: var(--gold) !important;
  opacity: 0.85;
}

.villa-card-name {
  font-family: var(--serif);
  font-size: 1.4rem;
  font-weight: 400;
  color: var(--text);
  letter-spacing: 0.01em;
}

.split-section {
  display: grid;
  grid-template-columns: 1fr 1fr;
  min-height: 640px;

  &.reverse { direction: rtl; > * { direction: ltr; } }
}

.split-image {
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    transition: transform 1s var(--ease);
  }

  &:hover img { transform: scale(1.03); }
}

.split-body {
  background: var(--layer);
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 5rem clamp(2.5rem, 7vw, 6rem);
  border-top: 1px solid var(--line);
  border-bottom: 1px solid var(--line);
}

.split-title {
  font-family: var(--serif);
  font-size: clamp(2.8rem, 5vw, 4.5rem);
  font-weight: 300;
  line-height: 1.05;
  letter-spacing: -0.02em;
  color: var(--text);
  margin-bottom: 1.5rem;

  em { font-style: italic; color: var(--gold); font-weight: 300; }
}

.split-line {
  width: 36px;
  height: 1px;
  background: var(--gold);
  opacity: 0.5;
  margin-bottom: 1.6rem;
}

.split-text {
  font-size: 0.97rem;
  line-height: 1.85;
  color: var(--muted);
  max-width: 46ch;
  margin-bottom: 2.4rem;
}

.destinations-section {
  position: relative;
  min-height: 600px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.destinations-bg {
  position: absolute;
  inset: 0;
  background-image: url(/public/chryssi.webp);
  background-size: cover;
  background-position: center 55%;
  z-index: 0;
  transition: transform 1s var(--ease);
}

.destinations-section:hover .destinations-bg {
  transform: scale(1.02);
}

.destinations-overlay {
  position: absolute;
  inset: 0;
  z-index: 1;
  background: linear-gradient(
    135deg,
    rgba(12,10,8,0.88) 0%,
    rgba(12,10,8,0.55) 100%
  );
}

.destinations-content {
  position: relative;
  z-index: 2;
  text-align: center;
  padding: 4rem 2rem;
  max-width: 700px;
}

.destinations-title {
  font-family: var(--serif);
  font-size: clamp(3.5rem, 7vw, 6.5rem);
  font-weight: 300;
  color: var(--text);
  line-height: 0.95;
  letter-spacing: -0.02em;
  margin-bottom: 1.6rem;

  em { font-style: italic; color: var(--gold); font-weight: 300; }
}

.destinations-sub {
  font-size: 0.95rem;
  color: rgba(237,230,216,0.55);
  line-height: 1.7;
  margin-bottom: 2.8rem;
  max-width: 45ch;
  margin-left: auto;
  margin-right: auto;
}

.cta-section {
  background: var(--layer);
  border-top: 1px solid var(--line);
  border-bottom: 1px solid var(--line);
  padding: 9rem 2.5rem;
  text-align: center;
}

.cta-inner {
  max-width: 600px;
  margin: 0 auto;
}

.cta-title {
  font-family: var(--serif);
  font-size: clamp(3rem, 6vw, 5.5rem);
  font-weight: 300;
  color: var(--text);
  line-height: 1;
  letter-spacing: -0.02em;
  margin-bottom: 1.4rem;

  em { font-style: italic; color: var(--gold); font-weight: 300; }
}

.cta-sub {
  font-size: 0.97rem;
  color: var(--muted);
  line-height: 1.75;
  margin-bottom: 3rem;
  max-width: 44ch;
  margin-left: auto;
  margin-right: auto;
}

.cta-actions {
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
}

@media (max-width: 1100px) {
  .villas-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .villa-card.featured {
    grid-row: span 1;
  }

  .villa-card-img {
    aspect-ratio: 4/3 !important;
    height: auto !important;
    min-height: unset !important;
  }
}

@media (max-width: 768px) {
  .villas-section { padding: 5rem 1.25rem; }

  .villas-grid {
    grid-template-columns: 1fr;
    gap: 1px;
  }

  .split-section,
  .split-section.reverse {
    grid-template-columns: 1fr;
    direction: ltr;
  }

  .split-image { aspect-ratio: 16/9; }

  .split-body { padding: 3.5rem 1.5rem; }

  .split-text { max-width: unset; }

  .intro-strip { gap: 0.8rem; }
}
</style>
